import type { CharacterChoice } from '../characters/characterSelection';
import { el } from './dom';
import { ICONS, icon } from './icons';

/** Card picture size (px) of the generated portraits – reserves the space before the image arrives. */
const PORTRAIT_SIZE = { width: 320, height: 400 } as const;

/**
 * Character cards (start and game-over screen): picture (shown by the mobile carousel), name, motto, a ✓
 * advantage and a ✗ disadvantage line; the selected card is highlighted. Click/tap selects – the arrow keys
 * are handled by the game (InputAction.MoveLeft/MoveRight), which calls setSelected().
 */
export class CharacterSelect {
  readonly element: HTMLDivElement;
  private readonly cards = new Map<string, HTMLButtonElement>();

  constructor(parent: HTMLElement, choices: readonly CharacterChoice[], onSelect: (id: string) => void, extraClass = '') {
    this.element = el('div', `char-select ${extraClass}`.trim(), undefined, parent);
    this.element.setAttribute('role', 'radiogroup');
    this.element.setAttribute('aria-label', 'Spielfigur wählen');
    for (const choice of choices) this.cards.set(choice.id, this.card(choice, onSelect));
  }

  setSelected(id: string): void {
    for (const [cardId, card] of this.cards) {
      const selected = cardId === id;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-checked', String(selected));
    }
  }

  private card(choice: CharacterChoice, onSelect: (id: string) => void): HTMLButtonElement {
    const card = el('button', 'char-card', undefined, this.element);
    card.type = 'button';
    card.setAttribute('role', 'radio');
    card.setAttribute('aria-checked', 'false');
    if (choice.art) {
      const image = el('img', 'char-card-image', undefined, card);
      image.src = choice.art.portraitUrl;               // loads with the screens – no pop-in when the menu shows
      image.alt = choice.art.alt;
      image.width = PORTRAIT_SIZE.width;
      image.height = PORTRAIT_SIZE.height;
      image.decoding = 'async';
    }
    const text = el('span', 'char-card-text', undefined, card);
    el('span', 'char-card-name', choice.name, text);
    if (choice.motto) el('span', 'char-card-motto', choice.motto, text);
    this.perkLine(text, 'char-card-pro', ICONS.advantage, 'Vorteil', choice.advantage);
    this.perkLine(text, 'char-card-con', ICONS.disadvantage, 'Nachteil', choice.disadvantage);
    card.addEventListener('click', (event) => {
      event.stopPropagation();
      onSelect(choice.id);
    });
    // Touch: selecting a card must not also count as the tap that starts the run.
    card.addEventListener('pointerdown', (event) => event.stopPropagation());
    return card;
  }

  private perkLine(parent: HTMLElement, className: string, mark: string, label: string, text: string): void {
    if (!text) return;
    const line = el('span', `char-card-line ${className}`, undefined, parent);
    line.append(icon(mark, 'char-card-mark'));
    const body = el('span', undefined, undefined, line);
    el('span', 'visually-hidden', `${label}: `, body);   // screen readers: the icons only show it
    body.append(text);
  }
}
